import React, { useEffect, useState } from "react";
import { Modal } from "rsuite";
import { toast } from "react-toastify";
import { Equipament, EquipamentFormData } from "../../../../interfaces/Equipament";
import api from "../../../../services/axios";

interface ClientOption {
  id: number;
  legalName: string;
  tradeName: string;
}

interface ModalAddEditComputerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  computer: Equipament | null;
  onSave: () => void;
}

const initialFormData: EquipamentFormData = {
  serieNumber: "",
  model: "",
  year: new Date().getFullYear(),
  purchaseDate: "",
  processor: "",
  memory: "",
  storage: "",
  status: "disponivel",
  actualUser: "",
  initialDate: "",
  payerClientId: null,
  monthlyValue: null,
};

const ModalAddEditComputer: React.FC<ModalAddEditComputerProps> = ({
  open,
  setOpen,
  computer,
  onSave,
}) => {
  const [formData, setFormData] = useState<EquipamentFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const [clients, setClients] = useState<ClientOption[]>([]);

  const isEditing = computer !== null;

  useEffect(() => {
    if (!open) return;
    const loadClients = async () => {
      try {
        const res = await api.get<ClientOption[]>("/Client/Clients");
        setClients((res.data ?? []).filter((c: any) => c.flActive));
      } catch {
        // silent — clients dropdown stays empty if fail
      }
    };
    loadClients();
  }, [open]);

  useEffect(() => {
    if (open) {
      if (computer) {
        setFormData({
          serieNumber: computer.serieNumber,
          model: computer.model,
          year: computer.year,
          purchaseDate: computer.purchaseDate?.split("T")[0] || "",
          processor: computer.processor,
          memory: computer.memory,
          storage: computer.storage,
          status: computer.status,
          actualUser: computer.actualUser || "",
          initialDate: computer.initialDate?.split("T")[0] || "",
          payerClientId: computer.payerClientId,
          monthlyValue: computer.monthlyValue,
        });
      } else {
        setFormData(initialFormData);
      }
    }
  }, [open, computer]);

  const handleClose = () => {
    setOpen(false);
    setFormData(initialFormData);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "year" ? parseInt(value) || 0 : value,
      // Equipamento com usuário não está "disponível": preencher o usuário promove o
      // status automaticamente (o usuário ainda pode trocar para "danificado" depois).
      ...(name === "actualUser" && value.trim() && prev.status === "disponivel"
        ? { status: "em_uso" as const }
        : {}),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validação básica
      if (!formData.model || !formData.serieNumber || !formData.processor) {
        toast.error("Por favor, preencha todos os campos obrigatórios.");
        setLoading(false);
        return;
      }

      const payload = {
        ...formData,
        actualUser: formData.actualUser || null,
        initialDate: formData.initialDate || null,
        payerClientId: formData.payerClientId ?? null,
        monthlyValue:
          formData.monthlyValue === null || formData.monthlyValue === undefined
            ? null
            : Number(formData.monthlyValue),
      };

      if (isEditing) {
        // Atualizar computador existente
        await api.put(`/Equipament`, { id: computer!.id, ...payload });
        toast.success("Equipamento atualizado com sucesso!");
      } else {
        // Criar novo computador
        await api.post("/Equipament", payload);
        toast.success("Equipamento cadastrado com sucesso!");
      }

      handleClose();
      onSave();
    } catch (error: any) {
      console.error("Error saving computer:", error);
      toast.error(
        error.response?.data?.message || "Erro ao salvar equipamento."
      );
    } finally {
      setLoading(false);
    }
  };

  const years = Array.from(
    { length: 20 },
    (_, i) => new Date().getFullYear() - i
  );

  const labelCls = "text-sm font-semibold text-gray-700 dark:text-gray-300";
  const fieldCls =
    "mt-1.5 w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-800 transition-all focus:border-dv-blue-500 focus:outline-none focus:ring-2 focus:ring-dv-blue-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

  return (
    <Modal open={open} size="lg" onClose={handleClose}>
      <Modal.Header>
        <Modal.Title>
          <b className="text-lg font-extrabold text-navy-700 dark:text-white">
            {isEditing ? "Editar Equipamento" : "Novo Equipamento"}
          </b>
        </Modal.Title>
      </Modal.Header>
      <form onSubmit={handleSubmit}>
        <Modal.Body>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Serial Number */}
            <div className="flex flex-col">
              <label htmlFor="serieNumber" className={labelCls}>
                Número de Série *
              </label>
              <input
                id="serieNumber"
                type="text"
                name="serieNumber"
                value={formData.serieNumber}
                onChange={handleChange}
                className={fieldCls}
                placeholder="Ex: SN123456789"
                required
              />
            </div>

            {/* Model */}
            <div className="flex flex-col">
              <label htmlFor="model" className={labelCls}>Modelo *</label>
              <input
                id="model"
                type="text"
                name="model"
                value={formData.model}
                onChange={handleChange}
                className={fieldCls}
                placeholder="Ex: Dell Latitude 5520"
                required
              />
            </div>

            {/* Year */}
            <div className="flex flex-col">
              <label htmlFor="year" className={labelCls}>Ano *</label>
              <select
                id="year"
                name="year"
                value={formData.year}
                onChange={handleChange}
                className={fieldCls}
                required
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>

            {/* Purchase Date */}
            <div className="flex flex-col">
              <label htmlFor="purchaseDate" className={labelCls}>
                Data de Compra *
              </label>
              <input
                id="purchaseDate"
                type="date"
                name="purchaseDate"
                value={formData.purchaseDate}
                onChange={handleChange}
                className={fieldCls}
                required
              />
            </div>

            {/* Processor */}
            <div className="flex flex-col">
              <label htmlFor="processor" className={labelCls}>
                Processador *
              </label>
              <input
                id="processor"
                type="text"
                name="processor"
                value={formData.processor}
                onChange={handleChange}
                className={fieldCls}
                placeholder="Ex: Intel Core i7-1165G7"
                required
              />
            </div>

            {/* Memory */}
            <div className="flex flex-col">
              <label htmlFor="memory" className={labelCls}>Memória *</label>
              <input
                id="memory"
                type="text"
                name="memory"
                value={formData.memory}
                onChange={handleChange}
                className={fieldCls}
                placeholder="Ex: 16GB DDR4"
                required
              />
            </div>

            {/* Storage */}
            <div className="flex flex-col">
              <label htmlFor="storage" className={labelCls}>
                HD/Armazenamento *
              </label>
              <input
                id="storage"
                type="text"
                name="storage"
                value={formData.storage}
                onChange={handleChange}
                className={fieldCls}
                placeholder="Ex: 512GB SSD"
                required
              />
            </div>

            {/* Status */}
            <div className="flex flex-col">
              <label htmlFor="status" className={labelCls}>Status *</label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleChange}
                className={fieldCls}
                required
              >
                <option value="disponivel">Disponível</option>
                <option value="em_uso">Em Uso</option>
                <option value="danificado">Danificado</option>
              </select>
            </div>

            {/* Cliente Pagador (rental) */}
            <div className="flex flex-col">
              <label htmlFor="payerClientId" className={labelCls}>
                Cliente Pagador (aluguel)
              </label>
              <select
                id="payerClientId"
                name="payerClientId"
                value={formData.payerClientId ?? ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    payerClientId: e.target.value === "" ? null : Number(e.target.value),
                  }))
                }
                className={fieldCls}
              >
                <option value="">— sem aluguel —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.tradeName} ({c.legalName})
                  </option>
                ))}
              </select>
            </div>

            {/* Valor Mensal */}
            <div className="flex flex-col">
              <label htmlFor="monthlyValue" className={labelCls}>
                Valor Mensal (R$)
              </label>
              <input
                id="monthlyValue"
                type="number"
                name="monthlyValue"
                min="0"
                step="0.01"
                value={formData.monthlyValue ?? ""}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    monthlyValue: e.target.value === "" ? null : Number(e.target.value),
                  }))
                }
                className={fieldCls}
                placeholder="Ex: 300.00"
              />
            </div>

            {/* Posse: sempre visíveis — antes só apareciam com status "Em Uso", e não dava
                para registrar com quem o equipamento está ao cadastrar/editar. */}
            <div className="flex flex-col">
              <label htmlFor="actualUser" className={labelCls}>
                Usuário Atual (com quem está)
              </label>
              <input
                id="actualUser"
                type="text"
                name="actualUser"
                value={formData.actualUser}
                onChange={handleChange}
                className={fieldCls}
                placeholder="Nome do colaborador"
              />
              <p className="mt-1 text-[11px] text-gray-700 dark:text-gray-400">
                Preencher muda o status para "Em Uso" e entra no histórico de movimentação.
              </p>
            </div>

            <div className="flex flex-col">
              <label htmlFor="initialDate" className={labelCls}>
                Data de Início da Posse
              </label>
              <input
                id="initialDate"
                type="date"
                name="initialDate"
                value={formData.initialDate}
                onChange={handleChange}
                className={fieldCls}
              />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 px-5 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:bg-gray-50 active:scale-[.98] dark:border-white/15 dark:text-gray-200 dark:hover:bg-navy-700"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="dv-sheen flex items-center justify-center gap-1.5 rounded-xl bg-dv-gradient px-5 py-2.5 text-sm font-bold text-white shadow-dv-glow transition-all active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? "Salvando..." : isEditing ? "Atualizar" : "Cadastrar"}
            </button>
          </div>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default ModalAddEditComputer;
